import React from "react"
import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from "@mui/material"
import { useParams } from "react-router"
import ExpandMore from "@mui/icons-material/ExpandMore"
import { AlertProps } from '@mui/material/Alert';

import { httpGet } from "../Web"
import { UserContext } from "../UserProvider"
import { useSnackbar } from '../../contexts/SnackBarContext';

import { Interface } from "../types/Interface"
import { getWorkshopInterfaces } from "../types/Workshop"
import { WorkshopInterface } from "../types/WorkshopInterface"
import { WorkshopProduct, getWorkshopProducts } from "../types/WorkshopProducts"
import { Product, getProducts } from "../types/Product"
import { Company } from "../types/Company"
import { getWorkshopProductInterfaces } from "../types/WorkshopProductInterface"
import { ProductInterface } from "../types/ProductInterface";
import { WorkshopResult, WorkshopTestCaseResult, WorkshopResultCounters, getWorkshopResultsForWorkshop, getResultCounters, updateWsResultConv } from "../types/WorkshopResult"
import { WorkshopTestCase, WorkshopTestCaseMap, getWSProductTestcases } from "../types/WorkshopTestCases";
import { getWSTestCaseResult, updateWSTestCaseResult } from "../types/WorkshopResult";
import { InterfaceAccordion, InterfacesToProperties } from "./WSTesting/InterfaceAccordian";

import { getWorkshopUsers } from '../types/WorkshopUser';
import { TestCase, getTestCases } from '../types/TestCase';
import { TestCombination, TestComboSupportVal, getTestComboSupport, getTestCombinationList } from '../types/TestCombinations';
import { WorkshopProductCombination, getWSProductCombinations } from '../types/WorkshopProductCombination';

import { PageAlternate, BreadCrumbProps } from "../subs/PageAlternate";
import { isGlobalAdmin } from '../types/User';
import { TestingInfoTable } from "./WSTestingStart/TestingInfoTable"
import { TestingTreeView } from "./WSTestingStart/TestingTreeView"
import { TestingStartPageHeader } from "./WSTestingStart/TestingStartPageHeader"
import { WorkshopProductForm } from "./WSTesting/WorkshopProductForm"
import { GroupedTestCases2Accordion } from "./WSTestingStart/GroupedTestCases"

export const WSTestingStart = () => {
    const { id, disabled } = useParams();
    const userContext = React.useContext(UserContext);
    const { showSnackbar } = useSnackbar();

    const [workshopId, setWorkshopId] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [readOnlyMode, setReadOnlyMode] = React.useState<boolean>(true);
    const [readyToTest, setReadyToTest] = React.useState<boolean>(false);
    const [filterDisabled, setFilterDisabled] = React.useState<boolean>(false);

    const [workshopProducts, setWorkshopProducts] = React.useState<WorkshopProduct[]>([]);
    const [workshopResultCounters, setWorkshopResultCounters] = React.useState<WorkshopResultCounters[]>([]);
    const [products, setProducts] = React.useState<Product[]>([]);
    const [companies, setCompanies] = React.useState<Company[]>([]);
    const [workshopInterfaces, setWorkshopInterfaces] = React.useState<WorkshopInterface[]>([]);
    const [workshopProductInterfaces, setWorkshopProductInterfaces] = React.useState<Map<number, ProductInterface[]>>(new Map<number, ProductInterface[]>());
    const [interfaces, setInterfaces] = React.useState<Interface[]>([]);
    const [interfacesProperties, setInterfacesProperties] = React.useState<InterfacesToProperties[]>([]);
    const [workshopTestcaseMap, setworkshopTestcaseMap] = React.useState<WorkshopTestCaseMap>();

    const [userCompany, setUserCompany] = React.useState<Company | undefined>();
    const [selectedInterface, setSelectedInterface] = React.useState<Interface | undefined>();
    const [currentWsTcResults, setCurrentWsTcResults] = React.useState<WorkshopTestCaseResult[]>([]);

    const [selectedServerProduct, setSelectedServerProduct] = React.useState<[Product, WorkshopProduct, WorkshopProductCombination] | undefined>();
    const [selectedClientProduct, setSelectedClientProduct] = React.useState<[Product, WorkshopProduct, WorkshopProductCombination] | undefined>();
    const [testCases, setTestCases] = React.useState<Map<number, TestCase[]>>(new Map < number, TestCase[]>());

    const [workshopResults, setWorkshopResults] = React.useState<WorkshopResult[]>([]);

    const [allWsProdCombinations, setAllWsProdCombinations] = React.useState<WorkshopProductCombination[]>([]);
    const [wsProdCombinations, setWsProdCombinations] = React.useState<WorkshopProductCombination[]>([]);
    const [testCombinations, setTestCombinations] = React.useState<TestCombination[]>([]);

    const [validWSInterfaces, setValidWSInterfaces] = React.useState<ProductInterface[]>([]);
    const [validTestCases, setValidTestCases] = React.useState<Map<number, TestCase[]>>(new Map<number, TestCase[]>());
    const [consumerWSTestcases, setConsumerWSTestcases] = React.useState<WorkshopTestCase[]>([]);
    const [providerWSTestcases, setProviderWSTestcases] = React.useState<WorkshopTestCase[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    // after 60 secs of idle (or not recording new results) refresh the results
    React.useEffect(() => {
        console.log(`initializing user account check`);
        const interval = setInterval(() => {
            httpGet('/api/account/checkloginstatus', new AbortController());
        }, 60000);

        return () => {
            console.log(`clearing user account check Timer`);
            clearInterval(interval);
        };
    }, []);

    const changeTestCaseResultHandler = (testCase: TestCase, updatedWsResult: WorkshopTestCaseResult) => {
        saveResultsHandler(testCase, updatedWsResult);
    }

    const saveResultsHandler = async (testCase: TestCase, updatedWsResult: WorkshopTestCaseResult) => {
        const abortController = new AbortController();
        if (!selectedInterface) return;
        let workshopResultsLoc = workshopResults;
        let currentWsResult = workshopResultsLoc.find(wsr => wsr.clientId === selectedClientProduct?.[1].id
            && wsr.serverId === selectedServerProduct?.[1].id && wsr.interfaceId === selectedInterface.id
            && wsr.testCombinationId === selectedServerProduct[2].testCombinationId) as WorkshopResult;
        let newResult = { ...updatedWsResult, testCaseId: testCase.id, workshopResultId: currentWsResult?.id };

        // if the result is new, we need to set the ids for insertion
        if (!currentWsResult) {
            showUserFeedback("Internal Error! Please go back to Config and mark as 'Ready to Test' again");
            return;
        }
        if (currentWsResult.id === 0) {
            if (typeof selectedClientProduct !== 'undefined' && typeof selectedServerProduct !== 'undefined' && typeof selectedInterface !== 'undefined') {
                currentWsResult.clientId = selectedClientProduct[1].id;
                currentWsResult.serverId = selectedServerProduct[1].id;
                currentWsResult.testCombinationId = selectedServerProduct[2].testCombinationId;
                currentWsResult.interfaceId = selectedInterface.id;
                currentWsResult.workshopId = Number(id);
            }
            else {
                showUserFeedback('Error: No products selected');
                return;
            }
        }

        // update the test case result
        let response = await updateWSTestCaseResult(newResult, abortController);
        if (response.failed) {
            showUserFeedback(response.errorText);
            return;
        }

        // now update the results header, this will chain update the all results (if support changed) and the counters
        let wsResultsResponse = await updateWsResultConv(currentWsResult, abortController);
        if (wsResultsResponse.failed) {
            showUserFeedback(wsResultsResponse.errorText);
            return;
        }
        currentWsResult = wsResultsResponse.result;

        if (currentWsResult && currentWsResult.id) {
            // get the updated results
            let wsTestcasesresponse = await getWSTestCaseResult(currentWsResult.id, new AbortController());
            if (wsTestcasesresponse.failed) {
                showUserFeedback(wsTestcasesresponse.errorText);
                return;
            }
            const updatedWSTCR: WorkshopTestCaseResult[] = wsTestcasesresponse.result;
            setCurrentWsTcResults(updatedWSTCR);
        }

        refreshResults();
    };

    const refreshResults = React.useCallback(async () => {
        const abortController = new AbortController();
        const wsResultsResponse = await getWorkshopResultsForWorkshop(Number(id), abortController);

        if (wsResultsResponse.failed) {
            showUserFeedback(wsResultsResponse.errorText);
            return;
        }
       
        let workshopResultsLoc = wsResultsResponse.result;

        workshopResultsLoc = workshopResultsLoc.filter(wr =>
            wsProdCombinations.find(wp => wp.workshopProductId === wr.clientId && wp.testCombinationId === wr.testCombinationId) ||
            wsProdCombinations.find(wp => wp.workshopProductId === wr.serverId && wp.testCombinationId === wr.testCombinationId));
        setWorkshopResults(workshopResultsLoc);

        const workshopResultCountersResponse = await getResultCounters(Number(id), abortController);
        if (workshopResultCountersResponse.failed) {
            showUserFeedback(workshopResultCountersResponse.errorText);
            return;
        }
        let workshopResultCountersLoc = workshopResultCountersResponse.result;
        setWorkshopResultCounters(workshopResultCountersLoc);

    }, [id, wsProdCombinations, showUserFeedback]);

    const fetch = React.useCallback(async (id: number) => {
        try {
            if (id === 0) return;

            console.log('****WSTestingStart: init begin: ');

            setIsLoading(true);
            const sharedAbortController = new AbortController();
            let promiseResults: Promise<any>[] = [];

            let globalAdminFlag = await isGlobalAdmin(userContext.user.id!!, new AbortController());
            if (!globalAdminFlag) {
                const workshopUsersResponse = await getWorkshopUsers(id, new AbortController());
                if (!workshopUsersResponse.failed) {
                    globalAdminFlag = workshopUsersResponse.result.find(wu => wu.userId === userContext.user.id)?.isWorkshopAdmin ?? false;
                }
            }
            const wsInterfacesResponse = getWorkshopInterfaces(id, sharedAbortController);
            promiseResults.push(wsInterfacesResponse);
            const wsProductsResponse = getWorkshopProducts(id, sharedAbortController);
            promiseResults.push(wsProductsResponse);
            const productsResponse = getProducts(sharedAbortController);
            promiseResults.push(productsResponse);
            const wsResultsResponse = getWorkshopResultsForWorkshop(id, sharedAbortController);
            promiseResults.push(wsResultsResponse);
            const companiesResponse = httpGet('/api/company', new AbortController());
            promiseResults.push(companiesResponse);
            const wsProdCombinationsResponse = getWSProductCombinations(Number(id), sharedAbortController);
            promiseResults.push(wsProdCombinationsResponse);
            const testCombinationsResponse = getTestCombinationList(sharedAbortController);
            promiseResults.push(testCombinationsResponse);
            const workshopResultCountersResponse = getResultCounters(Number(id), sharedAbortController);
            promiseResults.push(workshopResultCountersResponse);

            const responses = await Promise.all(promiseResults);
            const filterDisabledLoc = globalAdminFlag;
            setFilterDisabled(filterDisabledLoc);

            let someFailed = responses.find(response => response.failed);

            if (someFailed) {
                showUserFeedback(someFailed.errorText);
                return;
            }

            const workshopInterfacesLoc = (await wsInterfacesResponse).result;
            setWorkshopInterfaces(workshopInterfacesLoc);

            const workshopProductsLoc = (await wsProductsResponse).result;
            setWorkshopProducts(workshopProductsLoc);

            const testCombinationsLoc = (await testCombinationsResponse).result;
            setTestCombinations(testCombinationsLoc);

            const workshopResultCountersLoc = (await workshopResultCountersResponse).result;
            setWorkshopResultCounters(workshopResultCountersLoc);

            let productsLoc = (await productsResponse).result;
            productsLoc = productsLoc.filter(prod => workshopProductsLoc.find(wp => wp.productId === prod.id));
            setProducts(productsLoc);

            let workshopResultsLoc = (await wsResultsResponse).result;

            let companiesLoc = (await companiesResponse);
            const userCompanyLoc = companiesLoc.result.find((company: Company) => userContext.user.companyName === company.name);

            let wsProdCombinationsLoc = (await wsProdCombinationsResponse).result;
            wsProdCombinationsLoc = wsProdCombinationsLoc.filter(wpc => wpc.isConsumer || wpc.isProvider);
            setAllWsProdCombinations(wsProdCombinationsLoc);
            setUserCompany(userCompanyLoc);
            setCompanies(companiesLoc.result.filter((company: Company) => productsLoc.find(prod => prod.companyId === company.id)));

            const companyWorkshopProductsLoc = filterDisabledLoc ? workshopProductsLoc : workshopProductsLoc.filter(wp => productsLoc.find(prod => prod.id === wp.productId)?.companyId === userCompanyLoc?.id);

            wsProdCombinationsLoc = wsProdCombinationsLoc.filter(wpc => companyWorkshopProductsLoc.find(wp => wp.id === wpc.workshopProductId));
            setWsProdCombinations(wsProdCombinationsLoc);

            workshopResultsLoc = workshopResultsLoc.filter(wr =>
                wsProdCombinationsLoc.find(wp => wp.workshopProductId === wr.clientId && wp.testCombinationId === wr.testCombinationId) ||
                wsProdCombinationsLoc.find(wp => wp.workshopProductId === wr.serverId && wp.testCombinationId === wr.testCombinationId));
            setWorkshopResults(workshopResultsLoc);

            console.log('***WSTestingStart: results: ' + workshopResultsLoc.length);

            const testCaseResponses = await Promise.all(workshopInterfacesLoc.map(iface => getTestCases(iface.interfaceId, new AbortController())));
            someFailed = testCaseResponses.find(response => response.failed);

            if (someFailed) {
                console.log('FailedResponse: ', someFailed);
                showUserFeedback(someFailed.errorText);
                return;
            }
            // convert workshopProductInterfaceResponse into a ProductInterfaceMap
            const TestcasesLoc: Map<number, TestCase[]> = new Map();
            testCaseResponses.forEach(response => {
                if (response.result[0]) TestcasesLoc.set(response.result[0]?.interfaceId, response.result);
            });
            setTestCases(TestcasesLoc);

            // get the workshop test cases
            const wsTestCaseResponses = await Promise.all(workshopProductsLoc.map(wsProd => getWSProductTestcases(wsProd.id, new AbortController())));
            someFailed = wsTestCaseResponses.find(response => response.failed);

            if (someFailed) {
                console.log('FailedResponse: ', someFailed);
                showUserFeedback(someFailed.errorText);
                return;
            }
            // convert wsTestCaseResponses into a WorkshopTestCaseMap
            const workshopTestcaseMapLoc: WorkshopTestCaseMap = new Map();
            wsTestCaseResponses.forEach(response => {
                if (response.result[0]) workshopTestcaseMapLoc.set(response.result[0]?.workshopProductId, response.result);
            });
            setworkshopTestcaseMap(workshopTestcaseMapLoc);

            // get the workshop product interfaces
            const workshopProductInterfaceResponse = await Promise.all(workshopProductsLoc.map(wsProd => getWorkshopProductInterfaces(wsProd.id, new AbortController())));
            someFailed = workshopProductInterfaceResponse.find(response => response.failed);

            if (someFailed) {
                console.log('FailedResponse: ', someFailed);
                showUserFeedback(someFailed.errorText);
                return;
            }
            // convert workshopProductInterfaceResponse into a ProductInterfaceMap
            const productInterfacesLoc: Map<number, ProductInterface[]> = new Map();
            workshopProductInterfaceResponse.forEach(response => {
                if (response.result[0]) productInterfacesLoc.set(response.result[0]?.workshopProductId, response.result);
            });
            setWorkshopProductInterfaces(productInterfacesLoc);

            console.log('****WSTestingStart: init end: ');

            setIsLoading(false);
        } catch (error) {
            showUserFeedback(String(error));
        }
    }, [showUserFeedback, userContext]);

    // after 60 secs of idle (or not recording new results) refresh the results
    React.useEffect(() => {
        console.log(`initializing Result Refresh Timer`);
        const interval = setInterval(() => {
            refreshResults();
        }, 60000);

        return () => {
            console.log(`clearing Result Refresh Timer`);
            clearInterval(interval);
        };
    }, [refreshResults]);

    React.useEffect(() => {
        if (Number(id) === 0) return;
        if (Number(id) === workshopId) return;
        setWorkshopId(Number(id));

        fetch(Number(id));
    }, [id, fetch, workshopId]);

    // get Interfaces and remove interfaces for which there is no corresponding WorkshopInterface
    React.useEffect(() => {
        async function getInterfaces() {
            let interfaces = await httpGet('/api/interface', new AbortController());

            if (interfaces.failed || interfaces.result === undefined) {
                showUserFeedback(interfaces.errorText);
                return;
            }
            setInterfaces(interfaces.result.filter((iface: Interface) => workshopInterfaces.find(wi => wi.interfaceId === iface.id)));
        }

        getInterfaces();
    }, [workshopInterfaces]);

    React.useEffect(() => {
        let interfacesToProperties: InterfacesToProperties[] = [];

        for (let iface of interfaces) {
            httpGet(`/api/interface/property/${iface.id}`, new AbortController())
                .then((answer) => { interfacesToProperties.push({ iface, properties: answer.result }); });
        }

        setInterfacesProperties(interfacesToProperties);
    }, [interfaces]);


    const getWorkshopTestcaseResults = async (wsResultID: number) => {
        const wsTestCaseResultResponses = await getWSTestCaseResult(wsResultID, new AbortController());

        if (wsTestCaseResultResponses.failed) {
            console.log('FailedResponse: ', wsTestCaseResultResponses);
            showUserFeedback(wsTestCaseResultResponses.errorText);
            return;
        }
        setCurrentWsTcResults(wsTestCaseResultResponses.result);
    }

    React.useEffect(() => {
        const fetch = async () => {
            if (!selectedServerProduct || !selectedClientProduct || selectedServerProduct[2].testCombinationId === 0) {
                setValidWSInterfaces([]);
                setProviderWSTestcases([]);
                setConsumerWSTestcases([]);
                setValidTestCases(new Map<number, TestCase[]>());
                return; // no test combination selected
            }

            const testCombinationId = selectedServerProduct[2].testCombinationId;
            let testSupportSearch: TestComboSupportVal = { testCombinationId: testCombinationId }
            const testComboSupportedResponse = await getTestComboSupport(testSupportSearch, new AbortController());

            if (testComboSupportedResponse.failed) {
                setValidWSInterfaces([]);
                setProviderWSTestcases([]);
                setConsumerWSTestcases([]);
                setValidTestCases(new Map<number, TestCase[]>());
                showUserFeedback(testComboSupportedResponse.errorText);
                return;
            }
            const testComboSupported = testComboSupportedResponse.result;
            let validWSInterfacesLoc: ProductInterface[] = [];
            let productInterfacesLoc: ProductInterface[] = workshopProductInterfaces?.get(selectedServerProduct[1].id) ?? [];
            let consumerWorkshopTestcases: WorkshopTestCase[] = workshopTestcaseMap?.get(selectedServerProduct[1].id) ?? [];
            let consumerWSTestcasesLoc: WorkshopTestCase[] = [];
            let providerWorkshopTestcases: WorkshopTestCase[] = workshopTestcaseMap?.get(selectedClientProduct[1].id) ?? [];
            let providerWSTestcasesLoc: WorkshopTestCase[] = [];
            let validTestCasesLoc: Map<number, TestCase[]> = new Map<number, TestCase[]>();

            if (testCases) {
                testCases.forEach((tcList, ifaceId) => {
                    if (!validTestCasesLoc.has(ifaceId)) {
                        validTestCasesLoc.set(ifaceId, []);
                    }
                    tcList.forEach(tc => {
                        if (testComboSupported.find(tcs => tcs.interfaceId === ifaceId && tcs.testCaseId === tc.id)) {
                            validTestCasesLoc.get(ifaceId)?.push(tc);
                        }
                    });
                });
            };

            productInterfacesLoc.forEach(pi => {
                if (testComboSupported.find(tcs => tcs.interfaceId === pi.interfaceId && tcs.testCaseId === 0 && tcs.interfacePropertyId === 0)) {
                    validWSInterfacesLoc.push(pi);
                }
            });

            consumerWorkshopTestcases.forEach(wstc => {
                if (testComboSupported.find(tcs => tcs.testCombinationId === wstc.testCombinationId && tcs.testCaseId === wstc.testCaseId)) {
                    consumerWSTestcasesLoc.push(wstc);
                }
            });

            providerWorkshopTestcases.forEach(wstc => {
                if (testComboSupported.find(tcs => tcs.testCombinationId === wstc.testCombinationId && tcs.testCaseId === wstc.testCaseId)) {
                    providerWSTestcasesLoc.push(wstc);
                }
            });

            setValidWSInterfaces(validWSInterfacesLoc);
            setConsumerWSTestcases(consumerWSTestcasesLoc);
            setProviderWSTestcases(providerWSTestcasesLoc);
            setValidTestCases(validTestCasesLoc);
        }
        fetch();
    }, [testCases, workshopTestcaseMap, selectedServerProduct, selectedClientProduct, showUserFeedback, workshopProductInterfaces]);

    const commonSelectHandler = (nodeHierarchy: string[]) => {
        if (nodeHierarchy.length === 6) { //a product node has been selected
            let consumerWorkshopProductId = 0;
            let providerWorkshopProductId = 0;
            let selectedTestCombinationId = parseInt(nodeHierarchy[1]);
            let selectedInterfaceId = parseInt(nodeHierarchy[3]);

            if (nodeHierarchy[2] === 'C') {
                consumerWorkshopProductId = parseInt(nodeHierarchy[0]);
                providerWorkshopProductId = parseInt(nodeHierarchy[5]);
                setReadOnlyMode(false);
            }
            else {
                providerWorkshopProductId = parseInt(nodeHierarchy[0]);
                consumerWorkshopProductId = parseInt(nodeHierarchy[5]);
                setReadOnlyMode(true);      // server side selected first, so we are in read only mode
            }

            const consumerWorkshopProduct = workshopProducts.find(wp => wp.id === consumerWorkshopProductId) as WorkshopProduct;
            const consumerProduct = products.find(prod => prod.id === consumerWorkshopProduct?.productId) as Product;
            const providerWorkshopProduct = workshopProducts.find(wp => wp.id === providerWorkshopProductId) as WorkshopProduct;
            const providerProduct = products.find(prod => prod.id === providerWorkshopProduct.productId) as Product;
            const selectedInterface = interfaces.find(iface => iface.id === selectedInterfaceId) as Interface;
            const consumerWspTestCombination = allWsProdCombinations.find(tc => tc.testCombinationId === selectedTestCombinationId
                && tc.workshopProductId === consumerWorkshopProductId) as WorkshopProductCombination;
            const providerWspTestCombination = allWsProdCombinations.find(tc => tc.testCombinationId === selectedTestCombinationId
                && tc.workshopProductId === providerWorkshopProductId) as WorkshopProductCombination;
        
            if (consumerWorkshopProduct && providerWorkshopProduct && consumerProduct && providerProduct && selectedInterface && consumerWspTestCombination && providerWspTestCombination) {
                const resultId = workshopResults.find(wsr => wsr.clientId === consumerWorkshopProduct.id &&
                    wsr.serverId === providerWorkshopProduct.id && wsr.interfaceId === selectedInterfaceId &&
                    wsr.testCombinationId === selectedTestCombinationId)?.id;

                if (resultId !== undefined) getWorkshopTestcaseResults(resultId);
                setReadyToTest(consumerWspTestCombination.consumerReadyToTest && providerWspTestCombination.providerReadyToTest);
                setSelectedClientProduct([consumerProduct, consumerWorkshopProduct, consumerWspTestCombination]);
                setSelectedServerProduct([providerProduct, providerWorkshopProduct, providerWspTestCombination]);
                setSelectedInterface(selectedInterface);
                return;
            }
        }

        // deselected a product node
        setReadyToTest(false);
        setSelectedClientProduct(undefined);
        setSelectedServerProduct(undefined);
        setSelectedInterface(undefined);
    }

    const breadcrumbs: BreadCrumbProps[] = [
        { to: "/TestingTab", text: "Workshop Selection" },
        { to: `/WSTesting/${id}`, text: "Test Configuration  (Legacy)" },
        { to: `/wstestingrecords/${id}`, text: "Start Testing" },
    ];

    return <PageAlternate backButton
        heading={<TestingStartPageHeader product={selectedServerProduct?.[0]} workshopProduct={selectedServerProduct?.[1]}/>}
        sidePanel={<TestingTreeView ownCompany={userCompany!}
            filterDisabled={filterDisabled}
            companies={companies} parentLoading={isLoading}
            products={products} testCombinations={testCombinations}
            wsProdCombinations={allWsProdCombinations}
            workshopProducts={workshopProducts}
            workshopProductInterfaces={workshopProductInterfaces}
            workshopResultCounters={workshopResultCounters}
            interfaces={interfaces}
            onSelectHandler={commonSelectHandler} />}
            breadcrumbs={Boolean(disabled) ? undefined : breadcrumbs}>
        {[<Stack rowGap={20}>
            <TestingInfoTable client={selectedClientProduct} server={selectedServerProduct} iface={selectedInterface} companies={companies} />
            {(!selectedServerProduct && !selectedClientProduct) ? <Typography variant='h5'>Please select a product to get started</Typography> :
                <Typography variant='h5'>Testing information and results</Typography>}
            {selectedServerProduct && <>
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}><Typography variant='bold'>Server Information</Typography></AccordionSummary>
                    <AccordionDetails>
                        {<WorkshopProductForm disabled={true}
                            interfaces={interfaces}
                            selectedTreeType={"P"}
                            testCases={undefined}
                            workshopTestcases={[]}
                            workshopInterfaces={workshopInterfaces}
                            product={selectedServerProduct[0]}
                            workshopProduct={selectedServerProduct[1]}
                            productInterfaces={validWSInterfaces}
                            wpSubmitHandler={() => { }}
                            wpIfaceSubmitHandler={() => { }}
                            wsProdCombination={selectedServerProduct[2]}
                        />}
                    </AccordionDetails >
                    <InterfaceAccordion interfaceToProperties={interfacesProperties} workshopProductCombination={selectedServerProduct[2]}
                        productInterfaces={validWSInterfaces} workshopProduct={selectedServerProduct[1]} disabled={true} />
                </Accordion >
            </>}
            {
                ((readyToTest || disabled) && selectedInterface) ? 
                    selectedServerProduct && selectedClientProduct && <GroupedTestCases2Accordion
                    activeInterface={selectedInterface}
                    testCases={validTestCases.get(selectedInterface.id) ?? []}
                    clientWSTestcases={consumerWSTestcases}
                    serverWSTestcases={providerWSTestcases}
                    workshopTestCaseResults={currentWsTcResults}
                    disabledForm={Boolean(disabled) || readOnlyMode}
                    changeTestCaseResultHandler={changeTestCaseResultHandler} /> :
                <Typography variant='h5'>The selected products are not ready for testing</Typography>
            }
        </Stack >]}
    </PageAlternate>
}
